import { useState } from 'react';
import { scroller } from 'react-scroll';
import { noop } from '@belong/common';
import { ButtonBase, Image, Text } from '@belong/ui';
import clsx from 'clsx';
import Field from 'components/Field/Field';
import Form from 'components/Form/Form';
import { InputFinalFormAdapter } from 'components/Input/Input';
import TextBubbleWithUserProfile from 'components/TextBubbleWithUserProfile/TextBubbleWithUserProfile';
import { Satisfaction } from 'formcomponents/satisfaction/satisfaction';
import FormLayout from 'layouts/FormLayout/FormLayout';
import PropTypes from 'prop-types';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import PostInspectionContentLayout from '../../../PostInspectionContentLayout/PostInspectionContentLayout';
import { getPricingAssignedEmployee } from '../utils';
import css from './Strategy.module.css';

const GridItem = ({
  image,
  title,
  description,
  isBig,
}: {
  image: string;
  title: string;
  description: string;
  isBig?: boolean;
}) => {
  const [showDescription, setShowDescription] = useState(false);

  const toggleDescription = () => {
    setShowDescription(!showDescription);
  };

  // There is a bug where the images are not showing up in the correct resolution
  // Couldn't find the reason why, so we disable srcSet to fix it
  const imageFixProps = {
    srcSet: '',
    'data-srcset': '',
  };

  return (
    <div
      className={clsx(
        `${isBig ? 'md:col-span-6' : 'md:col-span-4'}`,
        'relative flex h-7xl w-full cursor-pointer flex-col md:h-auto',
        // CSS modules are declared as `Record<string, string>`, so every class
        // reads as `string | undefined` and cannot be a computed key. Asserting
        // keeps the existing behaviour; `className={css.x}` elsewhere is
        // unaffected because a possibly-undefined class name is valid there.
        { [css.isOpen!]: showDescription },
        css.gridItem,
      )}
      key={title}
      role="button"
      tabIndex={0}
      onClick={toggleDescription}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') toggleDescription();
      }}
    >
      <div className={clsx(css.cardInner)}>
        <div
          className={clsx(
            css.cardFront,
            'flex grow flex-col items-center justify-center overflow-hidden',
          )}
        >
          <div className="flex grow items-center justify-center overflow-hidden">
            <Image
              className={clsx('max-h-full max-w-full object-contain')}
              src={image}
              alt=""
              {...imageFixProps}
            />
          </div>
          <Text
            className="mt-sm text-center md:mt-lg"
            variant="body"
            fontWeight="semibold"
          >
            {title}
          </Text>
        </div>
        <div className={clsx(css.cardBack, 'bg-light-gray')}>
          <Text variant="body" fontWeight="regular">
            {description}
          </Text>
        </div>
      </div>
    </div>
  );
};

type Props = {
  onPrevious?: () => void;
  onSaveAndNext: (values: {
    userPriceStrategyFeedback: any;
    userPriceStrategySentiment: any;
  }) => void;
  stepConfig: any;
  stepConfigs: any;
  stepData: any;
  stepResource: any;
};

const Strategy = ({
  stepResource,
  onSaveAndNext,
  stepConfigs,
  stepConfig,
  stepData,
  onPrevious: _onPrevious,
}: Props) => {
  const assignedEmployee = getPricingAssignedEmployee(stepResource);

  const gridItems = [
    {
      image: '/pricing-flow/major_sites.svg',
      title: POST_INSPECTION_FLOW_STRINGS['pricing.strategy.all-major-sites'],
      description:
        POST_INSPECTION_FLOW_STRINGS[
          'pricing.strategy.all_major_sites_description'
        ],
      isBig: true,
    },
    {
      image: '/pricing-flow/touring_your_home.png',
      title: POST_INSPECTION_FLOW_STRINGS['pricing.strategy.touring-your-home'],
      description:
        POST_INSPECTION_FLOW_STRINGS[
          'pricing.strategy.touring-your-home_description'
        ],
    },
    {
      image: '/pricing-flow/zillow_preferential_partnership.svg',
      title:
        POST_INSPECTION_FLOW_STRINGS[
          'pricing.strategy.zillow-preferential-partnership'
        ],
      description:
        POST_INSPECTION_FLOW_STRINGS[
          'pricing.strategy.zillow-preferential-partnership_description'
        ],
    },
    {
      image: '/pricing-flow/dedicated_team_outreach.png',
      title:
        POST_INSPECTION_FLOW_STRINGS[
          'pricing.strategy.dedicated-team-outreach'
        ],
      description:
        POST_INSPECTION_FLOW_STRINGS[
          'pricing.strategy.dedicated-team-outreach_description'
        ],
      isBig: true,
    },
  ];

  function onSubmit(values: any) {
    onSaveAndNext({
      userPriceStrategySentiment: values.strategySentiment,
      userPriceStrategyFeedback: values.strategyFeedback,
    });
  }

  function getInitialValues() {
    return {
      strategySentiment: stepData?.userPriceStrategySentiment,
      strategyFeedback: stepData?.userPriceStrategyFeedback,
    };
  }

  return (
    <>
      <PostInspectionContentLayout
        showCityBackground={false}
        noOverlay
        stepConfigs={stepConfigs}
        stepConfig={stepConfig}
        title={POST_INSPECTION_FLOW_STRINGS['pricing.strategy.title']}
        employee={assignedEmployee}
      >
        <div className="h-auto w-full">
          <Image
            className="sm:hidden"
            src="/pricing-flow/strategy-graph-m.svg"
            alt="Strategy Graph"
          />
          <Image
            className="hidden sm:block"
            src="/pricing-flow/strategy-graph.svg"
            alt="Strategy Graph"
          />
        </div>
        <div>
          <div className="mt-xl grid grid-cols-1 gap-lg md:grid-cols-2">
            <div className="col-span-1">
              <Text fontWeight="semibold">
                {
                  POST_INSPECTION_FLOW_STRINGS[
                    'pricing.strategy.first-4-days-title'
                  ]
                }
              </Text>
              <Text variant="p1" className="mt-xs">
                {
                  POST_INSPECTION_FLOW_STRINGS[
                    'pricing.strategy.first-4-days-description'
                  ]
                }
              </Text>
            </div>
            <div className="col-span-1">
              <Text fontWeight="semibold">
                {POST_INSPECTION_FLOW_STRINGS['pricing.strategy.capped-title']}
              </Text>
              <Text variant="p1" className="mt-xs">
                {
                  POST_INSPECTION_FLOW_STRINGS[
                    'pricing.strategy.capped-description'
                  ]
                }
              </Text>
            </div>
          </div>
          <div className="mt-xl flex items-start gap-xs rounded border border-solid border-green bg-green-light-bg p-sm md:items-center">
            <div style={{ width: 60 }} className="shrink-0">
              <Image src="/pricing-flow/pricing_report_ic_noti.svg" alt="" />
            </div>
            <Text>
              {
                POST_INSPECTION_FLOW_STRINGS[
                  'pricing.strategy.notified-callout'
                ]
              }
            </Text>
          </div>
        </div>

        <Form
          onSubmit={onSubmit}
          initialValues={getInitialValues()}
          getForm={({ values, handleSubmit }) => {
            const hasNegativeSentiment = values.strategySentiment === 'Bad';

            const ctaText = hasNegativeSentiment
              ? POST_INSPECTION_FLOW_STRINGS[
                  'pricing.strategy.bad-sentiment-cta'
                ]
              : POST_INSPECTION_FLOW_STRINGS['pricing.strategy.cta'];

            return (
              <FormLayout>
                <Text
                  variant="h3"
                  fontWeight="semibold"
                  className="mt-3xl md:mt-2xl"
                >
                  {
                    POST_INSPECTION_FLOW_STRINGS[
                      'pricing.strategy.sentiment-title'
                    ]
                  }
                </Text>
                <div className="mt-xl md:mt-sm">
                  <Satisfaction
                    name="strategySentiment"
                    onChangeCustom={() => {
                      setTimeout(() => {
                        scroller.scrollTo('strategyFeedbackSection', {
                          duration: 1000,
                          smooth: true,
                          offset: -100,
                        });
                      }, 300);
                    }}
                    fluid
                    validate={noop}
                    withMarginTop={false}
                  />
                </div>
                {hasNegativeSentiment && (
                  <>
                    <div
                      className="mt-3xl md:mt-2xl"
                      id="strategyFeedbackSection"
                    >
                      <TextBubbleWithUserProfile
                        text={
                          POST_INSPECTION_FLOW_STRINGS[
                            'pricing.strategy.employee_reminder'
                          ]
                        }
                        employee={assignedEmployee}
                      />
                    </div>
                    <div className="mt-2xl">
                      <Field
                        name="strategyFeedback"
                        component={InputFinalFormAdapter}
                        placeholder={
                          POST_INSPECTION_FLOW_STRINGS[
                            'pricing.strategy.tell-us-how-you-feel'
                          ]
                        }
                        withMarginTop={false}
                      />
                    </div>
                  </>
                )}

                <div className="my-2xl flex w-full md:hidden">
                  <ButtonBase
                    className="rounded h-2xl flex-1 bg-green disabled:bg-gray"
                    onClick={handleSubmit}
                  >
                    <Text fontWeight="semibold" className="text-white">
                      {ctaText}
                    </Text>
                  </ButtonBase>
                </div>

                <div className="mt-2xl hidden justify-center md:flex">
                  <PostInspectionContentLayout.CtaButton onClick={handleSubmit}>
                    {ctaText}
                  </PostInspectionContentLayout.CtaButton>
                </div>
              </FormLayout>
            );
          }}
        />
        <div className="my-3xl grid grid-cols-1 gap-sm md:grid-cols-10">
          <div className="col-span-1 md:col-span-10">
            <Text variant="h3" fontWeight="semibold">
              {
                POST_INSPECTION_FLOW_STRINGS[
                  'pricing.strategy.marketing-message'
                ]
              }
            </Text>
          </div>
          {gridItems.map((gridItem) => (
            <GridItem key={gridItem.title} {...gridItem} />
          ))}
        </div>
      </PostInspectionContentLayout>
    </>
  );
};
Strategy.propTypes = {
  stepResource: PropTypes.object.isRequired,
  onSaveAndNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  stepConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
  stepConfig: PropTypes.object.isRequired,
  stepData: PropTypes.object.isRequired,
};

export default Strategy;
